import {
	ApiKeysPanel,
	authApi,
	cavortAuthKeys,
	SessionsPanel,
	TotpSetupPage,
	useAuth,
} from "@cavort/auth";
import {
	RiComputerLine,
	RiKeyLine,
	RiLockPasswordLine,
	RiNotification3Line,
	RiShieldKeyholeLine,
	RiUserLine,
} from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";

export const Route = createFileRoute("/_app/profile")({
	component: ProfilePage,
});

function getInitials(name?: string, email?: string): string {
	if (name) {
		const [first, second] = name.split(" ").filter(Boolean);
		if (first && second) return (first.charAt(0) + second.charAt(0)).toUpperCase();
		return name.slice(0, 2).toUpperCase();
	}
	if (email) return email.slice(0, 2).toUpperCase();
	return "U";
}

type Tab = "general" | "password" | "security" | "api-keys" | "sessions" | "notifications";

const profileTabs = [
	{ key: "general" as const, label: "General", icon: RiUserLine },
	{ key: "password" as const, label: "Password", icon: RiLockPasswordLine },
	{ key: "security" as const, label: "Security", icon: RiShieldKeyholeLine },
	{ key: "api-keys" as const, label: "API Keys", icon: RiKeyLine },
	{ key: "sessions" as const, label: "Sessions", icon: RiComputerLine },
	{
		key: "notifications" as const,
		label: "Notifications",
		icon: RiNotification3Line,
	},
];

function ProfilePage() {
	const [tab, setTab] = useState<Tab>("general");

	return (
		<div className="space-y-4">
			<div>
				<h1 className="text-2xl font-bold tracking-tight">Profile</h1>
				<p className="text-muted-foreground">Manage your personal account settings.</p>
			</div>

			<div className="flex gap-1 border-b overflow-x-auto">
				{profileTabs.map((t) => (
					<button
						key={t.key}
						type="button"
						onClick={() => setTab(t.key)}
						className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
							tab === t.key
								? "border-primary text-primary"
								: "border-transparent text-muted-foreground hover:text-foreground"
						}`}
					>
						<t.icon className="size-4" />
						{t.label}
					</button>
				))}
			</div>

			{tab === "general" && <GeneralTab />}
			{tab === "password" && <PasswordTab />}
			{tab === "security" && <SecurityTab />}
			{tab === "api-keys" && <ApiKeysTab />}
			{tab === "sessions" && <SessionsTab />}
			{tab === "notifications" && <ProfileNotificationsTab />}
		</div>
	);
}

// ── General Tab ─────────────────────────────────────────────────────

function GeneralTab() {
	const queryClient = useQueryClient();
	const { user: profile, isLoading } = useAuth();

	const [name, setName] = useState("");
	const [nameInitialized, setNameInitialized] = useState(false);
	const [savingName, setSavingName] = useState(false);

	if (profile && !nameInitialized) {
		setName(profile.name ?? "");
		setNameInitialized(true);
	}

	const initials = getInitials(profile?.name, profile?.email);

	async function handleSaveName() {
		if (!name.trim()) return;
		setSavingName(true);
		try {
			await authApi.updateProfile({ name: name.trim() });
			toast.info("Profile updated");
			await queryClient.invalidateQueries({ queryKey: cavortAuthKeys.me() });
		} catch (e) {
			toast.error(e instanceof Error ? e.message : "Failed to update profile");
		} finally {
			setSavingName(false);
		}
	}

	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<Skeleton className="h-5 w-40" />
					<Skeleton className="h-4 w-64" />
				</CardHeader>
				<CardContent className="space-y-4">
					<Skeleton className="h-16 w-16 rounded-full" />
					<Skeleton className="h-9 w-full max-w-sm" />
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>Your Profile</CardTitle>
				<CardDescription>
					This is your personal information across all organizations.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-6">
				<div className="flex items-center gap-4">
					<Avatar className="size-16">
						<AvatarFallback className="text-lg font-bold">{initials}</AvatarFallback>
					</Avatar>
					<div>
						<p className="font-medium">{profile?.name ?? "User"}</p>
						<p className="text-sm text-muted-foreground">{profile?.email}</p>
						<div className="mt-1 flex gap-1.5">
							{profile?.isAdmin && <Badge variant="default">Superadmin</Badge>}
							<Badge variant="secondary" className="capitalize">
								{profile?.role}
							</Badge>
						</div>
					</div>
				</div>

				<div className="space-y-2 max-w-sm">
					<Label htmlFor="profile-name">Display name</Label>
					<Input
						id="profile-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="Your name"
					/>
				</div>
				<div className="space-y-2 max-w-sm">
					<Label>Email</Label>
					<Input value={profile?.email ?? ""} disabled className="bg-muted" />
					<p className="text-xs text-muted-foreground">
						Email cannot be changed. Contact support if needed.
					</p>
				</div>

				<Button onClick={handleSaveName} disabled={savingName || !name.trim()}>
					{savingName ? "Saving..." : "Save changes"}
				</Button>
			</CardContent>
		</Card>
	);
}

// ── Password Tab ────────────────────────────────────────────────────

function PasswordTab() {
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [savingPassword, setSavingPassword] = useState(false);

	async function handleChangePassword() {
		if (newPassword !== confirmPassword) {
			toast.error("Passwords do not match");
			return;
		}
		if (newPassword.length < 8) {
			toast.error("Password must be at least 8 characters");
			return;
		}
		setSavingPassword(true);
		try {
			await authApi.changePassword({ currentPassword, newPassword });
			toast.success("Password changed");
			setCurrentPassword("");
			setNewPassword("");
			setConfirmPassword("");
		} catch (e) {
			toast.error(
				e instanceof Error ? e.message : "Failed to change password. Check your current password.",
			);
		} finally {
			setSavingPassword(false);
		}
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>Change Password</CardTitle>
				<CardDescription>Update your password. Must be at least 8 characters.</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="space-y-2 max-w-sm">
					<Label htmlFor="current-pw">Current password</Label>
					<Input
						id="current-pw"
						type="password"
						value={currentPassword}
						onChange={(e) => setCurrentPassword(e.target.value)}
						placeholder="Enter current password"
					/>
				</div>
				<div className="space-y-2 max-w-sm">
					<Label htmlFor="new-pw">New password</Label>
					<Input
						id="new-pw"
						type="password"
						value={newPassword}
						onChange={(e) => setNewPassword(e.target.value)}
						placeholder="Enter new password"
					/>
				</div>
				<div className="space-y-2 max-w-sm">
					<Label htmlFor="confirm-pw">Confirm new password</Label>
					<Input
						id="confirm-pw"
						type="password"
						value={confirmPassword}
						onChange={(e) => setConfirmPassword(e.target.value)}
						placeholder="Confirm new password"
					/>
					{confirmPassword && newPassword !== confirmPassword && (
						<p className="text-xs text-destructive">Passwords do not match</p>
					)}
				</div>
				<Button
					onClick={handleChangePassword}
					disabled={
						savingPassword ||
						!currentPassword ||
						!newPassword ||
						newPassword !== confirmPassword ||
						newPassword.length < 8
					}
				>
					{savingPassword ? "Changing..." : "Change password"}
				</Button>
			</CardContent>
		</Card>
	);
}

// ── Security Tab ────────────────────────────────────────────────────

function SecurityTab() {
	const { user, refetch } = useAuth();
	const totpEnabled = user?.totpEnabled ?? false;

	return (
		<Card>
			<CardHeader>
				<CardTitle>Two-Factor Authentication</CardTitle>
				<CardDescription>
					Add an extra layer of security to your account using an authenticator app.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<TotpSetupPage totpEnabled={totpEnabled} onStateChange={refetch} />
			</CardContent>
		</Card>
	);
}

// ── API Keys Tab ────────────────────────────────────────────────────

// Personal keys (`/auth/me/api-keys`, kind `user`): each key acts as the
// signed-in person. Listing, expiry, rotation and revoke are cavort-auth's.
function ApiKeysTab() {
	return (
		<Card>
			<CardHeader>
				<CardTitle>API Keys</CardTitle>
				<CardDescription>Personal API keys for programmatic access.</CardDescription>
			</CardHeader>
			<CardContent>
				<ApiKeysPanel kind="user" />
			</CardContent>
		</Card>
	);
}

// ── Sessions Tab ────────────────────────────────────────────────────

function SessionsTab() {
	return (
		<Card>
			<CardHeader>
				<CardTitle>Active Sessions</CardTitle>
				<CardDescription>
					Devices where you are currently signed in. Revoke any you don't recognise.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<SessionsPanel />
			</CardContent>
		</Card>
	);
}

// ── Notifications Tab ───────────────────────────────────────────────

interface PreferenceItem {
	type: string;
	label: string;
	category: string;
	inApp: boolean;
	email: boolean;
}

function ProfileNotificationsTab() {
	const [preferences, setPreferences] = useState<PreferenceItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		fetch("/api/v1/notifications/preferences/personal", {
			credentials: "include",
		})
			.then((r) => r.json())
			.then((data) => {
				setPreferences(data.preferences ?? []);
				setLoading(false);
			})
			.catch(() => setLoading(false));
	}, []);

	function updatePref(type: string, field: "inApp" | "email", value: boolean) {
		setPreferences((prev) => prev.map((p) => (p.type === type ? { ...p, [field]: value } : p)));
	}

	async function handleSave() {
		setSaving(true);
		try {
			const res = await fetch("/api/v1/notifications/preferences/personal", {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({
					preferences: preferences.map((p) => ({
						type: p.type,
						inApp: p.inApp,
						email: p.email,
					})),
				}),
			});
			if (res.ok) {
				const data = await res.json();
				setPreferences(data.preferences ?? []);
				toast.info("Notification preferences saved");
			} else {
				toast.error("Failed to save preferences");
			}
		} catch {
			toast.error("Failed to save preferences");
		} finally {
			setSaving(false);
		}
	}

	// Group by category
	const categories = [...new Set(preferences.map((p) => p.category))];

	return (
		<Card>
			<CardHeader>
				<CardTitle>Notification Preferences</CardTitle>
				<CardDescription>
					Choose which notifications you receive. These are your personal defaults — organizations
					can override them.
				</CardDescription>
			</CardHeader>
			<CardContent>
				{loading ? (
					<div className="space-y-3">
						{[1, 2, 3].map((i) => (
							<Skeleton key={i} className="h-10 w-full" />
						))}
					</div>
				) : (
					<div className="space-y-6">
						{categories.map((cat) => (
							<div key={cat}>
								<h3 className="text-sm font-medium text-muted-foreground mb-3">{cat}</h3>
								<div className="space-y-3">
									{preferences
										.filter((p) => p.category === cat)
										.map((pref) => (
											<div key={pref.type} className="flex items-center justify-between py-2">
												<span className="text-sm">{pref.label}</span>
												<div className="flex items-center gap-6">
													<label className="flex items-center gap-2 text-sm text-muted-foreground">
														<Switch
															checked={pref.inApp}
															onCheckedChange={(v) => updatePref(pref.type, "inApp", v)}
														/>
														In-app
													</label>
													<label className="flex items-center gap-2 text-sm text-muted-foreground">
														<Switch
															checked={pref.email}
															onCheckedChange={(v) => updatePref(pref.type, "email", v)}
														/>
														Email
													</label>
												</div>
											</div>
										))}
								</div>
							</div>
						))}
						<Button onClick={handleSave} disabled={saving}>
							{saving ? "Saving..." : "Save preferences"}
						</Button>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
